import React, { useEffect, useState } from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import Link from "@mui/joy/Link";
import Input from "@mui/joy/Input";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import CreateRoundedIcon from "@mui/icons-material/CreateRounded";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddIcon from "@mui/icons-material/Add";
import { useApi } from "../../Auth/api/ApiContext";
import { useAuth } from "../../Auth/utils/useAuth";
import { CreateUserFormElement, HolidayCalndrDto } from "../../types";
import { IconButton, Table, Tooltip } from "@mui/joy";
import { EditFormModal } from "../globalCards/ModalBox";
import { LoaderBox } from "../globalCards/LoaderBox";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "asc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function HolidayCalendarCard() {
  const {
    useUpdateHolidayCalendrMutation,
    useHolidayCalendarViewMutation,
    error,
  } = useApi();
  const [visibleColumns] = useState([
    {
      checked: true,
      width: 140,
      name: "Date",
    },
    {
      checked: true,
      width: 140,
      name: "Status",
    },
    {
      checked: true,
      width: 140,
      name: "Remark",
    },
  ]);
  const [rows, setRows] = useState<HolidayCalndrDto[]>([]);
  const [order, setOrder] = useState<Order>("desc");
  const [open, setOpen] = useState(false);
  const [holidayCalShow, setHolidayCalShow] = useState(false);
  const { showToast } = useAuth();
  const [holidayRemark, setHolidayRemark] = useState("");
  const [holidayStatus, setHolidayStatus] = useState<string>("HOLIDAY");
  const [holidayDate, setHolidayDate] = useState(new Date().toISOString());
  const currentDate = new Date();
  const startOfYear = new Date(currentDate.getFullYear(), 0, 1).toISOString();
  const endOfYear = new Date(currentDate.getFullYear() + 1, 0, 0).toISOString();
  const getHolidayData = {
    startDate: startOfYear,
    endDate: endOfYear,
    status: "HOLIDAY",
  };
  useEffect(() => {
    if (useHolidayCalendarViewMutation) {
      useHolidayCalendarViewMutation.mutate(getHolidayData);
    }
  }, []);

  useEffect(() => {
    if (useHolidayCalendarViewMutation.data) {
      const filterData = useHolidayCalendarViewMutation.data?.filter(
        (item: any) => item.remark !== null
      );
      setRows(filterData);
    } else if (error) {
      showToast(error, "danger");
    }

    if (useUpdateHolidayCalendrMutation.data) {
      showToast("Holiday calendar updated successfully", "success");
      useUpdateHolidayCalendrMutation.reset();
      useHolidayCalendarViewMutation.mutate(getHolidayData);
      setHolidayCalShow(false);
    } else if (error) {
      showToast(error, "danger");
    }
  }, [
    useHolidayCalendarViewMutation,
    useUpdateHolidayCalendrMutation.data,
    error,
  ]);
  const handleSaveHolidayCald = (
    event: React.FormEvent<CreateUserFormElement>
  ) => {
    event.preventDefault();
    const formElements = event.currentTarget.elements;
    if (!formElements.holidayDateTime.value) {
      showToast("Please select a date.", "warning");
      return;
    }

    const holidayConvertDate = new Date(formElements.holidayDateTime.value);
    useUpdateHolidayCalendrMutation.mutate({
      date: holidayConvertDate.toISOString(),
      remark: formElements.holidayRemark.value,
      status: holidayStatus,
    });
  };

  const convertDate = (dateStr: string): string => {
    const date = new Date(dateStr);

    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    };
    const formattedDate = date
      .toLocaleDateString("en-GB", options)
      .replace(",", "");
    return formattedDate;
  };
  const handleHolidayCalnBtn = () => {
    setHolidayDate(new Date().toISOString());
    setHolidayRemark("");
    setHolidayStatus("HOLIDAY");
    setHolidayCalShow(true);
  };

  const renderFilters = () => (
    <React.Fragment>
      {/* ----------------Holiday Calendar-------------- */}
      <Button sx={{ height: "20px" }} onClick={handleHolidayCalnBtn}>
        <AddIcon />
        Create Holiday
      </Button>
    </React.Fragment>
  );

  const handleCellCopy = (content: string | number) => {
    navigator.clipboard
      .writeText(String(content))
      .then(() => {
        showToast("Copied to clipboard!", "success");
      })
      .catch(() => {
        showToast("Failed to copy!", "danger");
      });
  };

  const renderCellContent = (content: string | number, maxWidth: string) => (
    <Tooltip title={content} placement="top">
      <Typography>
        <span
          style={{
            maxWidth,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
            cursor: "copy",
          }}
          onClick={() => handleCellCopy(content)}
        >
          {content}
        </span>
      </Typography>
    </Tooltip>
  );

  const handleEditBtn = (usrData: any) => {
    setHolidayCalShow(true);
    setHolidayDate(usrData.date);
    setHolidayRemark(usrData.remark);
    setHolidayStatus(usrData.status);
  };

  return (
    <React.Fragment>
      <Sheet
        className="SearchAndFilters-mobile"
        sx={{
          display: { xs: "flex", sm: "none" },
          my: 1,
          gap: 1,
        }}
      >
        <Input
          size="sm"
          placeholder="Search"
          startDecorator={<SearchIcon />}
          sx={{ flexGrow: 1 }}
        />
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          onClick={() => setOpen(true)}
        >
          <FilterAltIcon />
        </IconButton>
        <Modal open={open} onClose={() => setOpen(false)}>
          <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
            <ModalClose />
            <Typography id="filter-modal" level="h2">
              Filters
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Sheet sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {renderFilters()}
              <Button color="primary" onClick={() => setOpen(false)}>
                Submit
              </Button>
            </Sheet>
          </ModalDialog>
        </Modal>
      </Sheet>
      <Box
        className="SearchAndFilters-tabletUp"
        sx={{
          borderRadius: "sm",
          py: 2,
          display: { xs: "none", sm: "flex" },
          marginBottom: "6px",
          alignItems: "flex-end",
          alignSelf: "flex-end",
          flexWrap: "wrap",
          gap: 1.5,
          "& > *": {
            minWidth: { xs: "120px", md: "160px" },
          },
        }}
      >
        {/* ------------search------------ */}
        {renderFilters()}
      </Box>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "initial", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              {visibleColumns?.map((item, index) => (
                <th
                  key={index}
                  onClick={() => setOrder(order === "asc" ? "desc" : "asc")}
                  style={{
                    display: `${item.checked ? "table-cell" : "block"}`,
                    width: item.width,
                    padding: "12px 6px",
                  }}
                >
                  <Link
                    underline="none"
                    color="primary"
                    component="button"
                    fontWeight="lg"
                    endDecorator={<ArrowDropDownIcon />}
                    sx={{
                      "& svg": {
                        transition: "0.2s",
                        transform:
                          order === "asc" ? "rotate(180deg)" : "rotate(0deg)",
                      },
                    }}
                  >
                    {item.name}
                  </Link>
                </th>
              ))}
              <th style={{ width: 140, padding: "12px 6px", color: "#0b6bcb" }}>
                Edit
              </th>
            </tr>
          </thead>
          <tbody>
            {useHolidayCalendarViewMutation.isLoading ? (
              <LoaderBox message="Loading" />
            ) : (
              stableSort(rows, getComparator(order, "id")).map((row, index) => (
                <tr key={index}>
                  <td>
                    <Typography level="body-xs">
                      {renderCellContent(convertDate(row?.date), "140")}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {renderCellContent(row?.status, "140")}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-xs">
                      {renderCellContent(row?.remark, "140")}
                    </Typography>
                  </td>
                  <td>
                    <Box
                      sx={{
                        display: "flex",
                        marginLeft: "5px",
                        alignItems: "center",
                      }}
                    >
                      <Tooltip title="Edit" placement="top">
                        <Link level="body-xs" component="button">
                          <CreateRoundedIcon
                            sx={{
                              color: "coral",
                              fontSize: "20px",
                              borderRadius: "20px",
                              "&:hover": {
                                padding: "2px",
                                backgroundColor: "coral",
                                color: "white",
                                cursor: "pointer",
                              },
                            }}
                            onClick={() => handleEditBtn(row)}
                          />
                        </Link>
                      </Tooltip>
                    </Box>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>
      {/* ---------------Holiday--------------- */}
      <EditFormModal
        open={holidayCalShow}
        setOpen={setHolidayCalShow}
        title={"Holiday"}
        holidayDateTime={holidayDate}
        holidayRemarkCard={true}
        holidayRemark={holidayRemark}
        holidayStatus={holidayStatus}
        setSelectedStatus={setHolidayStatus}
        onSubmit={handleSaveHolidayCald}
      />
    </React.Fragment>
  );
}
